import type { EventEnvelope } from "@baton/api";
import type { CacheAction, Runtime } from "@baton/core";

/**
 * `contracts/client-api.md` § Invalidation from the event stream: the screen side of the rows whose
 * action reaches past the cache. The reducer's actions are applied to the cache by the applier; a
 * mounted screen that must also act (close a peek on `work_item.deleted`, follow `work_item.moved`,
 * render `no-access` when its project is archived or deleted, re-read a list row's `is_blocked`)
 * listens here for the envelope and the actions that were applied for it. Screens register while
 * they are mounted, so a screen that is not showing does nothing.
 */
export type ScreenEventListener = (envelope: EventEnvelope, actions: readonly CacheAction[]) => void;

const listeners = new WeakMap<Runtime, Set<ScreenEventListener>>();

export function listenScreenEvents(runtime: Runtime, listener: ScreenEventListener): () => void {
  let set = listeners.get(runtime);
  if (set === undefined) {
    set = new Set();
    listeners.set(runtime, set);
  }
  set.add(listener);
  return () => void set.delete(listener);
}

export function dispatchScreenEvent(runtime: Runtime, envelope: EventEnvelope, actions: readonly CacheAction[]): void {
  if (actions.length === 0) return;
  for (const listener of listeners.get(runtime) ?? []) listener(envelope, actions);
}
